import { t } from "@lingui/macro";
import {
  CreateExpenseItemDescriptionDocument,
  type CreateExpenseItemDescriptionMutation,
  type CreateExpenseItemDescriptionMutationVariables,
  type ExpenseItemInput,
} from "@src/__generated__/graphql";
import type { Store } from "@src/components/modules/expenses/Create/store";
import type { IOption } from "@src/components/ui-kit";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { denominate } from "@src/utils/formatters";
import type { TToFieldStates } from "@src/utils/forms/ts-utils";
import { required } from "@src/utils/forms/validators";
import { toExpenseDescriptionOptions } from "@src/utils/map-to-options/expense-descriptions";
import { FieldState, FormState } from "formstate";
import { action, computed, makeObservable, observable } from "mobx";

type Item = {
  id: string | undefined;
  amount_in_currency: string;
  global_credit_account_id: string | undefined;
  global_credit_account_title: string | undefined;
  accounting_account_id: string | undefined;
  accounting_centre_id: string | undefined;
  accounting_vat_classification_id: string | undefined;
  description_id: string | undefined;
  custom_cost_ratio: string | undefined;
};

export type CreditAccountItemFormState = FormState<TToFieldStates<Item>>;

export class ExpenseCreditAccountsCardStore {
  static readonly NEW_CREDIT_ACCOUNT_VALUE = "new-credit-account";

  @observable
  items: CreditAccountItemFormState[] = [];

  @observable.ref
  defaultCreditAccountOptions: IOption[] = [
    {
      value: ExpenseCreditAccountsCardStore.NEW_CREDIT_ACCOUNT_VALUE,
      label: t`New credit account`,
    },
  ];

  private expenseItemDescriptionMutator = new MutationHelper<
    CreateExpenseItemDescriptionMutation,
    CreateExpenseItemDescriptionMutationVariables
  >(CreateExpenseItemDescriptionDocument);

  constructor(private expenseStore: Store) {
    makeObservable(this);
  }

  @computed get costCentersOptions() {
    return this.expenseStore.costCentersOptions;
  }

  @computed get accountingAccountsOptions() {
    return this.expenseStore.accountingAccountsOptions;
  }

  @computed get descriptionOptions() {
    return toExpenseDescriptionOptions(this.expenseStore.itemDescriptions);
  }

  @action.bound
  addItem(item?: Partial<Item>): void {
    const creditAccountIdField = new FieldState(
      item?.global_credit_account_id,
    ).validators(required);

    this.items.push(
      new FormState({
        id: new FieldState<string | undefined>(item?.id),
        amount_in_currency: new FieldState(
          item?.amount_in_currency ?? "0",
        ).validators(required),
        global_credit_account_id: creditAccountIdField,
        global_credit_account_title: new FieldState(
          item?.global_credit_account_title,
        ).validators((value) => {
          if (
            creditAccountIdField.value ===
            ExpenseCreditAccountsCardStore.NEW_CREDIT_ACCOUNT_VALUE
          ) {
            return required(value);
          }
          return null;
        }),
        accounting_account_id: new FieldState(item?.accounting_account_id),
        accounting_centre_id: new FieldState(item?.accounting_centre_id),
        accounting_vat_classification_id: new FieldState(
          item?.accounting_vat_classification_id,
        ),
        description_id: new FieldState(item?.description_id),
        custom_cost_ratio: new FieldState(item?.custom_cost_ratio),
      }),
    );
  }

  @computed
  get ignoreCreditAccountOptionsIds(): string[] {
    return this.defaultCreditAccountOptions.map(({ value }) => value);
  }

  @action.bound
  addDefaultCreditAccountOptions(newOptions: IOption[]): void {
    this.defaultCreditAccountOptions =
      this.defaultCreditAccountOptions.concat(newOptions);
  }

  @action.bound
  setItems(items: Item[]): void {
    this.clearItems();
    for (const item of items) {
      this.addItem(item);
    }
  }

  @action.bound
  removeItem(index: number): void {
    this.items.splice(index, 1);
  }

  @action.bound
  clearItems(): void {
    this.items = [];
  }

  @action.bound
  async handleCreateDescription(title: string, itemIndex: number) {
    const [data, error] = await this.expenseItemDescriptionMutator.mutate({
      title,
    });

    if (error || !data?.createExpenseItemDescription) {
      console.error(error);
      return;
    }

    const newDescriptionOption = toExpenseDescriptionOptions([
      data.createExpenseItemDescription,
    ])[0];

    this.expenseStore.itemDescriptions =
      this.expenseStore.itemDescriptions.concat(
        data.createExpenseItemDescription,
      );

    this.items[itemIndex].$.description_id.onChange(newDescriptionOption.value);
    return newDescriptionOption;
  }

  @action.bound
  serialize(): ExpenseItemInput[] {
    return this.items.map((item) => {
      const isNewCreditAccount =
        item.$.global_credit_account_id.$ ===
        ExpenseCreditAccountsCardStore.NEW_CREDIT_ACCOUNT_VALUE;

      return {
        id: item.$.id.$,
        amount_in_currency: denominate(
          Number(item.$.amount_in_currency.$),
          this.expenseStore.selectedCurrency?.denomination ?? 2,
        ),
        global_credit_account_id: isNewCreditAccount
          ? undefined
          : item.$.global_credit_account_id.$,
        global_credit_account_title: isNewCreditAccount
          ? item.$.global_credit_account_title.$
          : undefined,
        accounting_account_id: item.$.accounting_account_id.$ ?? undefined,
        accounting_centre_id: item.$.accounting_centre_id.$ ?? undefined,
        accounting_vat_classification_id:
          item.$.accounting_vat_classification_id.$ ?? undefined,
        expense_item_description_id: item.$.description_id.$ ?? undefined,
        custom_cost_ratio: item.$.custom_cost_ratio.$ ?? undefined,
      };
    });
  }
}
